import { RootState } from '../../../../../rootReducer'
import {
  ACTIVE_TAB,
  CLASSICATION_TYPE,
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPES,
  DS_FILES_TABS,
  FILES_GROUP_BY,
  FILES_GROUP_BY_VALUES,
  FILTER_CLASS_KEY,
  FILTER_DANGER_API_VALUES,
  FILTER_DANGER_KEY,
  FILTER_LABEL_KEY,
  FILTER_SENSITIVITY,
  GROUP_BY,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_DATA_SOURCES,
  URL_DATA_SOURCES_STRUCTURED,
  URL_DOCUMENTS_ALL,
  URL_DOCUMENTS_CLASSIFIED,
  URL_DOCUMENTS_CLASSIFIED_CREATE,
  URL_FILES,
  URL_INSIGHTS,
  URL_LABEL_MANAGEMENT,
  URL_LABEL_SETS
} from '../../../../../constants'
import { DocumentLabel, LabelsWidgetParams } from '../../../../documentLabelsV2/types'
import { mockClassificationAndLabelWidgetData } from '../../../renderer/utils/widgetMockdata'
import { UNIQUE_WIDGET_INDEX, getUniqueWidgetIndex } from '../../constants'
import {
  ACTION_FETCH_LABELS_WIDGET,
  fetchLabelsWidget
} from '../../../../documentLabelsV2/documentLabelSlice'
import { FilterParams } from '../../../../../interfaces'
import {
  ACTION_CLASSIFICATIONS_GROUPED,
  ClassificationsGroupedParams,
  ClassificationsTopListByDataSource,
  FetchObjectsCountParams,
  fetchClassificationsGrouped,
  fetchObjectsCount
} from '../../../../classifications/classificationsSlice'
import useUrlContext from '../../../../../hooks/useUrlContext'
import { getShowLoader } from '../../../../../reducers/requestReducer'
import LoaderView from '../../../../../components/Loader/loader'
import { getUpdatedUrl } from '../../../../../utils/urlUtil'
import IconNoResults from '../../../../../assets/inlineSvg/icon-no-label-files.svg'
import { humanDiskSize } from '../../../../../utils/mathUtils'
import { DATA_SOURCE_UNSTRUCTURED_TYPES } from '../../../../../utils/dataSourceUtil'
import { getFeatureConfig } from '../../../../../configs/featureConfig'
import { Button, Icon, Tags, Typography } from '@lightbeamai/design-system'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import './classificationAndLabelingWidget.less'

interface OwnProps {
  showLoader: boolean
  isDashboard?: boolean
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

type DocumentDistributionWidget = {
  labelsData?: DocumentLabel[]
  classificationsData?: ClassificationsTopListByDataSource[]
  totalObjectsCount?: number
  sensitiveObjectsCount?: number
  duplicateObjectsCount?: number
  duplicateBytesCount?: number
}

interface StateProps {
  widgetData?: DocumentDistributionWidget
  showLoader: boolean
}

interface DispatchProps {
  fetchLabelsWidget: (params: LabelsWidgetParams & { [UNIQUE_WIDGET_INDEX]: string }) => void
  fetchObjectsCount: (params: FetchObjectsCountParams) => void
  fetchClassificationsGrouped: (
    filters: ClassificationsGroupedParams & { [UNIQUE_WIDGET_INDEX]: string }
  ) => void
}

type ClassificationAndLabelWidgetComponentProps = StateProps & DispatchProps & OwnProps

const ClassificationAndLabelWidget = (props: ClassificationAndLabelWidgetComponentProps) => {
  const {
    widgetData = {
      totalObjectsCount: 0,
      sensitiveObjectsCount: 0,
      labelsData: [],
      classificationsData: []
    },
    showLoader = false,
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDatasourceType,
    fetchLabelsWidget,
    fetchClassificationsGrouped,
    fetchObjectsCount
  } = props

  const intl = useIntl()

  const context = useUrlContext({ pageName: URL_DOCUMENTS_ALL })

  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID] || ''
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)

  /* Todo: move this to dashboard config */
  const showDuplicateData = useMemo(
    () =>
      widgetDatasourceType
        ? !getFeatureConfig(widgetDatasourceType).dashboard.widgetDocumentDistribution.duplicateData
            .isHidden
        : false,
    [widgetDatasourceType]
  )

  useEffect(() => {
    if (useMockData) return

    const filters: FilterParams = {
      filter: []
    }

    if (filters.filter) {
      datasourceId &&
        filters.filter.push({
          key: 'DATASOURCE_IDS',
          values: [datasourceId]
        })
    }

    fetchLabelsWidget({
      datasourceIds: datasourceId,
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })

    fetchClassificationsGrouped({
      datasourceId,
      filters,
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })

    fetchObjectsCount({
      datasourceId,
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
  }, [
    datasourceId,
    useMockData,
    widgetIndex,
    fetchLabelsWidget,
    fetchClassificationsGrouped,
    fetchObjectsCount
  ])

  const sortedLabellingData = useMemo(
    () =>
      widgetData.labelsData
        ?.slice()
        .filter((item) => (item?.objectCount ?? 0) > 0)
        .sort((a, b) => (b?.objectCount ?? 0) - (a?.objectCount ?? 0))
        .slice(0, 3) || [],
    [widgetData]
  )

  const totalLabelsCount = useMemo(
    () => widgetData.labelsData?.reduce((sum, labels) => sum + (labels?.objectCount ?? 0), 0) || 0,
    [widgetData]
  )

  const classificationsData = useMemo(() => {
    return useMockData
      ? mockClassificationAndLabelWidgetData.classificationWidgetData.classificationsByDs[0].list
      : widgetData.classificationsData?.find((item) => item.datasourceId === datasourceId)?.list ??
          []
  }, [datasourceId, useMockData, widgetData.classificationsData])

  const totalClassificationsCount = useMemo(
    () =>
      classificationsData?.reduce(
        (sum, labels) =>
          sum +
          ((labels.name !== CLASSICATION_TYPE.OTHERS &&
          labels.name !== CLASSICATION_TYPE.ATTENTION_NEEDED &&
          labels.name !== CLASSICATION_TYPE.UNCLASSIFIED &&
          labels.name !== CLASSICATION_TYPE.MISC
            ? labels?.count
            : 0) ?? 0),
        0
      ) || 0,
    [classificationsData]
  )

  const sortedClassificationsData = useMemo(() => {
    const classifications = classificationsData.slice().sort((a, b) => b.count - a.count)

    return classifications
      .filter((item) => item.count > 0)
      .filter(
        (c) =>
          c.name !== CLASSICATION_TYPE.OTHERS &&
          c.name !== CLASSICATION_TYPE.ATTENTION_NEEDED &&
          c.name !== CLASSICATION_TYPE.UNCLASSIFIED &&
          c.name !== CLASSICATION_TYPE.MISC
      )
      .slice(0, 3)
  }, [classificationsData])

  const baseDatasourceUrl = useMemo(() => {
    return DATA_SOURCE_UNSTRUCTURED_TYPES[widgetDatasourceType ?? DATA_SOURCE_TYPES.generic]
      ? URL_DATA_SOURCES
      : URL_DATA_SOURCES_STRUCTURED
  }, [widgetDatasourceType])

  const getLabelLink = ({
    groupBy,
    classification,
    label
  }: {
    groupBy: FILES_GROUP_BY_VALUES
    classification?: string
    label?: string
  }) => {
    const pageQueryParams = {
      [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list
    }

    if (label && groupBy === FILES_GROUP_BY.LABELS) {
      pageQueryParams[FILTER_LABEL_KEY] = label
    }

    if (classification && groupBy === FILES_GROUP_BY.CLASSIFICATION) {
      pageQueryParams[FILTER_CLASS_KEY] = classification
    }

    return getUpdatedUrl({
      pathName: datasourceId
        ? `${baseDatasourceUrl}/${datasourceId}${URL_FILES}`
        : URL_INSIGHTS + URL_DOCUMENTS_CLASSIFIED,
      pageName: URL_DOCUMENTS_CLASSIFIED,
      queryParams: {
        [GROUP_BY]: groupBy,
        ...(widgetDatasourceType ? { datasourceType: widgetDatasourceType } : {})
      },
      pageQueryParams
    })
  }

  const getAllLabelLink = ({
    groupBy = FILES_GROUP_BY.CLASSIFICATION,
    isSensitive = false,
    pageView = PAGE_VIEW_VALUES.list
  }: {
    groupBy?: FILES_GROUP_BY_VALUES
    isSensitive?: boolean
    pageView?: PAGE_VIEW_VALUES
  }) =>
    getUpdatedUrl({
      pathName: datasourceId
        ? `${baseDatasourceUrl}/${datasourceId}${URL_FILES}`
        : URL_INSIGHTS + URL_DOCUMENTS_CLASSIFIED,
      pageName: URL_DOCUMENTS_CLASSIFIED,
      queryParams: {
        ...(groupBy === FILES_GROUP_BY.DUPLICATES && datasourceId
          ? { [ACTIVE_TAB]: DS_FILES_TABS.DUPLICATE_FILES }
          : { [GROUP_BY]: groupBy }),
        ...(widgetDatasourceType ? { datasourceType: widgetDatasourceType } : {})
      },
      pageQueryParams: {
        [PAGE_VIEW_KEY]: pageView,
        ...(isSensitive
          ? {
              [datasourceId
                ? FILTER_SENSITIVITY
                : FILTER_DANGER_KEY]: FILTER_DANGER_API_VALUES.sensitive
            }
          : {})
      }
    })

  return (
    <div className="lb-widget-card lb-classification-and-labeling-widget">
      <LoaderView showLoader={showLoader} />
      <Typography type={'strong'}>
        {intl.formatMessage({ id: 'document.distribution.widget.title' })}
      </Typography>

      <div className="lb-files-count jc-space-between">
        <Link to={getAllLabelLink({ groupBy: FILES_GROUP_BY.LABELS })}>
          <Typography variant={'basePlus6'} type={'strong'} className="xs-m-0">
            {intl.formatNumber(widgetData.totalObjectsCount || 0, { notation: 'compact' })}
            <Typography variant={'baseMinus2'}>
              {intl.formatMessage({ id: 'gdrive.summary.files' })}
            </Typography>
          </Typography>
        </Link>
        <Link to={getAllLabelLink({ groupBy: FILES_GROUP_BY.LABELS, isSensitive: true })}>
          <Typography variant={'basePlus6'} type={'strong'} className="xs-m-0">
            {intl.formatNumber(widgetData.sensitiveObjectsCount || 0, { notation: 'compact' })}
            <Typography variant={'baseMinus2'}>
              {intl.formatMessage({ id: 'datasource.copilot.files' })}
            </Typography>
          </Typography>
        </Link>
        {datasourceId && showDuplicateData ? (
          <Link to={getAllLabelLink({ groupBy: FILES_GROUP_BY.DUPLICATES })}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography variant={'basePlus6'} type={'strong'} className="xs-m-0">
                {humanDiskSize(widgetData.duplicateBytesCount || 0, true, 1)}
              </Typography>
              <Tags
                color="information"
                size={20}
                style={{
                  backgroundColor: '#CBD0D8'
                }}
                icon={<Icon name="File05" size={18} />}
              >
                {intl.formatNumber(widgetData.duplicateObjectsCount || 0, { notation: 'compact' })}
              </Tags>
            </div>
            <Typography variant={'baseMinus2'}>
              {intl.formatMessage({ id: 'document.distribution.widget.duplicateData' })}
            </Typography>
          </Link>
        ) : null}
      </div>

      <div className="row xs-mt-16 lb-labels-row">
        <div className="lb-labels-data xs-pr-16">
          {sortedLabellingData.length > 0 ? (
            <>
              <Typography variant={'baseMinus2'} className="font-600">
                {intl.formatMessage({ id: 'document.distribution.widget.labels.title' })}
              </Typography>
              <div className="lb-labels-list">
                {sortedLabellingData.map((item) => (
                  <Link
                    className="lb-labels-item xs-mb-12"
                    key={item.id}
                    to={getLabelLink({ groupBy: FILES_GROUP_BY.LABELS })}
                    title={item.name}
                  >
                    <Typography variant={'baseMinus2'} className="xs-mb-0">
                      <span className="lb-label-item-text truncate">{item.name}</span> (
                      {intl.formatNumber(item.objectCount ?? 0, { notation: 'compact' })})
                    </Typography>
                    <div className="lb-progress-bar">
                      <div
                        className="lb-progress lb-progress-blue"
                        style={{ width: `${((item.objectCount || 0) / totalLabelsCount) * 100}%` }}
                      />
                    </div>
                  </Link>
                ))}
              </div>
              <Button
                variant="link"
                className="xs-p-0 lb-view-all-btn"
                size={24}
                href={getAllLabelLink({
                  groupBy: FILES_GROUP_BY.LABELS,
                  pageView: PAGE_VIEW_VALUES.card
                })}
              >
                <Typography variant={'baseMinus2'} type="medium" className="xs-m-0" color="custom">
                  {intl.formatMessage({ id: 'notifications.viewAll' })}
                </Typography>
              </Button>
            </>
          ) : (
            <div className="lb-no-data-screen xs-p-16">
              <IconNoResults />
              <Typography variant={'baseMinus2'} type={'medium'}>
                {intl.formatMessage({ id: 'document.distribution.widget.labels.noData' })}
              </Typography>
              <Button
                variant="link"
                className="xs-p-0 lb-view-all-btn"
                size={24}
                href={URL_INSIGHTS + URL_LABEL_MANAGEMENT + URL_LABEL_SETS}
              >
                <Typography variant={'baseMinus2'} type="medium" className="xs-m-0" color="custom">
                  {intl.formatMessage({ id: 'labels.labelManagement' })}
                </Typography>
              </Button>
            </div>
          )}
        </div>
        <div className="lb-classifications-data xs-pl-16">
          {sortedClassificationsData.length > 0 ? (
            <>
              <Typography variant={'baseMinus2'} className="font-600">
                {intl.formatMessage({ id: 'document.distribution.widget.classifications.title' })}
              </Typography>
              <div className="lb-labels-list">
                {sortedClassificationsData.map((item, index) => (
                  <Link
                    className="lb-labels-item xs-mb-12"
                    key={index}
                    to={getLabelLink({
                      groupBy: FILES_GROUP_BY.CLASSIFICATION,
                      classification: item.name
                    })}
                    title={item.name}
                  >
                    <Typography variant={'baseMinus2'} className="xs-mb-0">
                      <span className="lb-label-item-text truncate">{item.name}</span> (
                      {intl.formatNumber(item.count ?? 0, { notation: 'compact' })})
                    </Typography>
                    <div className="lb-progress-bar">
                      <div
                        className="lb-progress lb-progress-blue"
                        style={{
                          width: `${((item.count || 0) / totalClassificationsCount) * 100}%`
                        }}
                      />
                    </div>
                  </Link>
                ))}
              </div>
              <Button
                variant="link"
                className="xs-p-0 lb-view-all-btn"
                size={24}
                href={getAllLabelLink({
                  groupBy: FILES_GROUP_BY.CLASSIFICATION,
                  pageView: PAGE_VIEW_VALUES.card
                })}
              >
                <Typography variant={'baseMinus2'} type="medium" className="xs-m-0" color="custom">
                  {intl.formatMessage({ id: 'notifications.viewAll' })}
                </Typography>
              </Button>
            </>
          ) : (
            <div className="lb-no-data-screen xs-p-16">
              <IconNoResults />
              <Typography variant={'baseMinus2'} type={'medium'}>
                {intl.formatMessage({ id: 'document.distribution.widget.classifications.noData' })}
              </Typography>
              <Button
                variant="link"
                className="xs-p-0 lb-view-all-btn"
                size={24}
                href={URL_INSIGHTS + URL_DOCUMENTS_CLASSIFIED_CREATE}
              >
                <Typography variant={'baseMinus2'} type="medium" className="xs-m-0" color="custom">
                  {intl.formatMessage({ id: 'document.widget.option.classification' })}
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  showLoader:
    getShowLoader(ACTION_FETCH_LABELS_WIDGET) || getShowLoader(ACTION_CLASSIFICATIONS_GROUPED),
  //Using mock data to display sample content while adding a widget to the dasboard
  widgetData: ownProps?.useMockData
    ? {
        labelsData: mockClassificationAndLabelWidgetData.labellingWidgetData,
        classificationsData: mockClassificationAndLabelWidgetData.classificationWidgetData[0],
        totalObjectsCount: mockClassificationAndLabelWidgetData.totalObjectsCount,
        sensitiveObjectsCount: mockClassificationAndLabelWidgetData.sensitiveObjectsCount,
        duplicateObjectsCount: mockClassificationAndLabelWidgetData.duplicateObjectsCount,
        duplicateBytesCount: mockClassificationAndLabelWidgetData.duplicateBytesCount
      }
    : {
        labelsData:
          state.documentLabels.labelsWidgetData?.[
            getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
          ],
        classificationsData:
          state.classifications.widgetDataDocClassifications?.[
            getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
          ] || [],
        totalObjectsCount:
          state.classifications.totalObjectsCount?.[
            getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
          ],
        sensitiveObjectsCount:
          state.classifications.sensitiveObjectsCount?.[
            getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
          ],
        duplicateObjectsCount:
          state.classifications.duplicateObjectsCount?.[
            getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
          ],
        duplicateBytesCount:
          state.classifications.duplicateBytesCount?.[
            getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
          ]
      }
})

const mapDispatchToProps: DispatchProps = {
  fetchLabelsWidget,
  fetchClassificationsGrouped,
  fetchObjectsCount
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(ClassificationAndLabelWidget)
